/**
 * @fileoverview Custom OpenLayers control for region selection.
 * @module map/RegionControl
 */

import { DOM_ELEMENTS } from '../constants.js';
import { DropdownControl } from './DropdownControl.js';

const { CLASS_ACTIVE_FILTER } = DOM_ELEMENTS;

/**
 * Custom OpenLayers control that provides a dropdown menu for selecting a region filter.
 * Displays a geo icon button that reveals a dropdown with region options on click.
 * @class
 * @extends DropdownControl
 */
export class RegionControl extends DropdownControl {
    /**
     * Creates a new RegionControl instance.
     * @param {Object} [options={}] - Control options
     * @param {Array} [options.regions=[]] - Array of region objects with jregion and jlabel properties
     * @param {string} [options.selectedRegion=''] - Initially selected region ID
     * @param {Function} [options.onRegionChange] - Callback invoked when region changes: (regionId) => void
     * @param {HTMLElement} [options.target] - Target element to render the control into
     */
    constructor(options = {}) {
        super({
            buttonIconHtml: '<i class="bi bi-transparency"></i>',
            buttonClass: 'ol-region-control-button',
            buttonTitle: 'Neighborhoods',
            elementClass: 'ol-region-control',
            dropdownClass: 'region-control-dropdown',
            target: options.target
        });

        this.regions = options.regions || [];
        this.selectedRegion = options.selectedRegion || '';
        this.onRegionChange = options.onRegionChange || (() => {});

        // Populate dropdown
        this.buildDropdown();

        // Update button state based on initial selection
        this.updateButtonState();
    }

    /**
     * Builds the dropdown menu with region options.
     * @private
     */
    buildDropdown() {
        this.dropdown.innerHTML = '';

        // Create title bar at the top?
        /*
        const titleBar = document.createElement('div');
        titleBar.className = 'region-control-title';
        titleBar.textContent = 'SELECT A NEIGHBORHOOD TO HIGHLIGHT:';
        this.dropdown.appendChild(titleBar);
        */

        // Create "All Neighborhoods" option
        const allItem = this._createItem('', 'Clear neighborhood selection');
        this.dropdown.appendChild(allItem);

        // Create separator
        const separator = document.createElement('div');
        separator.className = 'region-control-separator';
        this.dropdown.appendChild(separator);

        // Create options for each region (sorted by jregion). A row with no jregion is left out: the
        // region is selected, highlighted and matched by that id, so such a row could only throw (no
        // jlabel either) or offer "undefined - X", whose choice sent undefined where '' means "no region".
        const isText = (value) => (typeof value === 'string' && value !== '');
        const sortedRegions = this.regions.filter(region => region && isText(region.jregion)).sort((a, b) => {
            return a.jregion.localeCompare(b.jregion);
        });

        for (const region of sortedRegions) {
            // nice label
            let label;
            if (isText(region.jlabel) && (region.jregion != region.jlabel)) {
                label = `${region.jregion} - ${region.jlabel}`;
            } else {
                if (region.jregion.length<4) {
                    label = `Neighborhood ${region.jregion}`;
                } else {
                    label = region.jregion;
                }
            }
            const item = this._createItem(region.jregion, label);
            this.dropdown.appendChild(item);
        }
    }

    /**
     * Creates a dropdown item element.
     * @param {string} value - The region ID value
     * @param {string} label - The display label
     * @returns {HTMLElement} The dropdown item element
     * @private
     */
    _createItem(value, label) {
        const item = document.createElement('div');
        item.className = 'region-control-item';
        if (value === this.selectedRegion) {
            item.classList.add('active');
        }
        item.textContent = label;
        item.dataset.value = value;
        item.addEventListener('click', (e) => {
            e.stopPropagation();
            this.selectRegion(value);
        });
        return item;
    }

    /**
     * Selects a region and triggers the callback.
     * @param {string} regionId - The region ID to select
     */
    selectRegion(regionId) {
        this.selectedRegion = regionId;
        this.updateButtonState();
        this.hideDropdown();
        this.buildDropdown(); // Rebuild to update active state
        this.onRegionChange(regionId);
    }

    /**
     * Updates the button appearance based on selection state.
     * @private
     */
    updateButtonState() {
        const button = this.element.querySelector('.ol-region-control-button');
        if (this.selectedRegion) {
            button.classList.add(CLASS_ACTIVE_FILTER);
        } else {
            button.classList.remove(CLASS_ACTIVE_FILTER);
        }
    }

    /**
     * Gets the currently selected region.
     * @returns {string} The selected region ID or empty string for all regions
     */
    getSelectedRegion() {
        return this.selectedRegion;
    }

    /**
     * Sets the selected region.
     * @param {string} regionId - The region ID to select
     */
    setSelectedRegion(regionId) {
        this.selectedRegion = regionId;
        this.updateButtonState();
        this.buildDropdown();
    }
}
